import { useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import northbeamLogoWhite from "@assets/northbeam-header-logo-white.svg";
import northbeamLogo from "@assets/northbeam-header-logo.svg";
import northbeamText from "@assets/northbeam-header-text.svg";
import { useAuth0 } from "@components/auth0-wrapper";
import { useUser } from "@components/user-context";
import { Badge, SecondaryButton } from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics/hooks";
import { useUniversalBenchmarksModalContext } from "@features/universal-benchmarks";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import { useToggleNBV2 } from "@hooks/use-toggle-nb-v2";
import { Menu } from "@mantine/core";
import {
  NAVBAR_QUERY,
  type Navbar as NavbarQuery,
} from "@north-beam/nb-client-side";
import { usePrefs } from "@utils/client-side-preferences";

import { AttributionModeWindowModal } from "./attribution-mode-window-modal";
import { CommandPalette } from "./command-palette";
import { ImpersonateClientSelect } from "./impersonate-client-select";
import { ModelSelectButton } from "./model-select-button";

interface Props {
  isExpanded: boolean;
  onToggle: () => void;
  onboarding?: boolean;
}

export const Topbar = ({ isExpanded, onToggle, onboarding }: Props) => {
  const { data } = useNorthbeamQuery<NavbarQuery>(NAVBAR_QUERY);
  const navigate = useNavigate();
  const { user } = useUser();

  const toggleNBV2 = useToggleNBV2();
  const [isSwitchingToOldUI, setIsSwitchingToOldUI] = useState(false);

  const prettyLogEventPage = usePrettyLogEventPage();

  const { logout } = useAuth0();
  const [attributionModeWindowModalOpen, setAttributionModeWindowModalOpen] =
    useState(false);

  const { openUniversalBenchmarksModal } = useUniversalBenchmarksModalContext();

  const textShadowStyle = {
    textShadow: "1px 1px 0px var(--v2-bg-primary, #FFF)",
  };

  const { hasFeatureFlag } = useFeatureFlags();
  const prefs = usePrefs();

  return (
    <div className="relative h-full flex flex-col bg-white z-[102]">
      <div className="h-full border-b border-v2-tertiary flex">
        <motion.div
          initial={false}
          animate={{
            width: isExpanded ? "240px" : "73px",
            backgroundColor: isExpanded ? "#F9FAFF00" : "#F9FAFFFF",
          }}
          transition={{ duration: 0.1 }}
          onClick={onToggle}
          className={clsx(
            "shrink-0 border-r border-v2-tertiary cursor-pointer overflow-hidden",
            "flex items-center justify-center",
          )}
        >
          {isExpanded ? (
            <motion.div
              key="animate-1"
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.1 }}
              className="w-full h-full pt-[21px] pl-[20px]"
            >
              <div className="flex items-center gap-2">
                <img
                  src={northbeamLogo}
                  alt="Northbeam Logo"
                  className="w-12 shrink-0"
                />
                <img
                  src={northbeamText}
                  alt="Northbeam Text"
                  className="w-[120px] shrink-0"
                />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="animate-2"
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.1 }}
              className="w-full h-full pl-3 pt-3 bg-v2-secondary"
            >
              <div className="rounded-full bg-v2-cosmic/400 w-12 h-12 flex items-center justify-center">
                <img
                  src={northbeamLogoWhite}
                  alt="Northbeam Logo"
                  className="w-8"
                />
              </div>
            </motion.div>
          )}
        </motion.div>
        <motion.div
          layout
          transition={{ duration: 0.1 }}
          className="flex-grow flex items-center justify-between px-5 bg-v2-primary"
        >
          <div className="flex flex-row items-center gap-2">
            <ImpersonateClientSelect data={data} />
            {!onboarding && (
              <Menu
                transitionProps={{ transition: "pop-top-right" }}
                position="top-start"
                withinPortal
              >
                <Menu.Target>
                  <div className="border hover:border-v2-cosmic/600 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="36"
                      height="36"
                      viewBox="0 0 36 36"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_2450_7468)">
                        <rect width="36" height="36" rx="18" fill="#F9FAFF" />
                        <rect
                          y="24"
                          width="36"
                          height="19"
                          rx="9.5"
                          fill="#010715"
                        />
                        <circle
                          cx="18"
                          cy="13"
                          r="10"
                          fill="#010715"
                          stroke="#F9FAFF"
                          strokeWidth="4"
                        />
                      </g>
                      <rect
                        x="1.5"
                        y="1.5"
                        width="33"
                        height="33"
                        rx="16.5"
                        stroke="white"
                        strokeWidth="3"
                      />
                      <defs>
                        <clipPath id="clip0_2450_7468">
                          <rect width="36" height="36" rx="18" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                </Menu.Target>
                <Badge
                  variant="alert"
                  className="border-v2-beta shadow-[0_2px_10px] shadow-v2-overlay-beta"
                  style={textShadowStyle}
                >
                  NB 2.0 BETA
                </Badge>
                <SecondaryButton
                  onClick={async () => {
                    prettyLogEventPage({
                      domain: "Dashboard V2",
                      action: "onClick",
                      additionalActionInfo: "Back to Old UI",
                    });
                    setIsSwitchingToOldUI(true);
                    await toggleNBV2();
                  }}
                  className="h-[24px] rounded border-v2-light px-2"
                >
                  <i className="fa-regular fa-arrow-left"></i>
                  Back to 1.0
                </SecondaryButton>
                <Menu.Dropdown className="bg-v2-primary border-v2-tertiary">
                  <Menu.Item
                    leftSection={
                      <i className="fa-regular fa-gear text-v2-action"></i>
                    }
                    onClick={() => navigate("settings/profile")}
                  >
                    <span className="text-v2-primary font-tt-commons-pro text-[14px] font-normal leading-[20px] tracking-[0.28px]">
                      Settings
                    </span>
                  </Menu.Item>
                  {hasFeatureFlag("enableUniversalBenchmarks") && (
                    <Menu.Item
                      onClick={() => {
                        openUniversalBenchmarksModal();
                      }}
                      leftSection={
                        <i className="fa-regular fa-dumbbell text-v2-action"></i>
                      }
                    >
                      <span className="text-v2-primary font-tt-commons-pro text-[14px] font-normal leading-[20px] tracking-[0.28px]">
                        Profitability Benchmarks
                      </span>
                    </Menu.Item>
                  )}
                  <Menu.Item
                    onClick={() => {
                      logout({
                        logoutParams: { returnTo: window.location.origin },
                      });
                    }}
                    leftSection={
                      <i className="fa-sharp-duotone fa-regular fa-arrow-right-from-bracket text-v2-action"></i>
                    }
                  >
                    <span className="text-v2-primary font-tt-commons-pro text-[14px] font-normal leading-[20px] tracking-[0.28px]">
                      Logout
                    </span>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </div>
          {!onboarding && <CommandPalette />}
          {!onboarding && (
            <ModelSelectButton
              onClick={() => setAttributionModeWindowModalOpen(true)}
            />
          )}
        </motion.div>
      </div>
      {!onboarding && (
        <AttributionModeWindowModal
          isOpen={attributionModeWindowModalOpen}
          onClose={() => setAttributionModeWindowModalOpen(false)}
        />
      )}
    </div>
  );
};
